// ═══════════════════════════════════════════════════════════════════════
// dataContractsTab.jsx — Field-level data contracts for every interface
// ═══════════════════════════════════════════════════════════════════════
// Each interface in IFACES has an `attrMap` array of {src, dst, xform, note,
// category} mappings. This tab makes those contracts browsable, searchable,
// and exportable. Source: CLM_DataContracts.pptx + IfacesData attrMap fields.
// ═══════════════════════════════════════════════════════════════════════

import React, { useState, useMemo } from "react";
import { IFACES, GRP_META, MVP_META } from "./IfacesData";
import { T, FONTS } from "./theme";
import {
  getAgentsForInterface, getBenefitsForInterface, getFlowsForInterface,
} from "./crossRefs";
import { EntityRefList } from "./navigation";

const CATEGORY_COLOR = {
  core:       T.green,
  quality:    T.amber,
  audit:      T.purple,
  security:   T.red,
  metadata:   T.steel,
  additional: T.cyan,
};

export default function DataContractsTab() {
  const [search, setSearch]               = useState("");
  const [filterGrp, setFilterGrp]         = useState("ALL");
  const [filterCat, setFilterCat]         = useState("ALL");
  const [selectedId, setSelectedId]       = useState(null);

  // Build a flat list of all interfaces with attrMap and a count
  const interfaces = useMemo(() => {
    return IFACES.map(f => ({
      ...f,
      contractFieldCount: (f.attrMap || []).length,
    })).filter(f => {
      if (filterGrp !== "ALL" && f.grp !== filterGrp) return false;
      if (search) {
        const hay = `${f.id} ${f.name} ${f.bo}`.toLowerCase();
        if (!hay.includes(search.toLowerCase())) return false;
      }
      return true;
    });
  }, [search, filterGrp]);

  const selected = selectedId ? IFACES.find(f => f.id === selectedId) : null;
  const selectedMap = selected?.attrMap || [];
  const selectedFiltered = selectedMap.filter(a => {
    if (filterCat !== "ALL" && (a.category || "additional") !== filterCat) return false;
    return true;
  });

  const totalFieldCount = interfaces.reduce((s, f) => s + f.contractFieldCount, 0);
  const withContracts   = interfaces.filter(f => f.contractFieldCount > 0).length;
  const groups          = [...new Set(IFACES.map(f => f.grp))].sort();
  const allCategories   = ["core", "quality", "audit", "security", "metadata", "additional"];

  return (
    <div style={{ padding: "20px 24px", maxHeight: "calc(100vh - 108px)", overflowY: "auto" }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: FONTS.header, fontSize: 16, fontWeight: 700, color: T.cyan, marginBottom: 4 }}>
          📋 CLM Data Contracts
        </div>
        <div style={{ fontSize: 11, color: T.text2 }}>
          Field-level source ↔ destination contracts for every interface · transforms · categories · notes
        </div>
      </div>

      {/* KPI strip */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        {[
          { l: "Interfaces",       v: IFACES.length, c: T.cyan },
          { l: "With contracts",   v: withContracts, c: T.green },
          { l: "Total fields",     v: totalFieldCount, c: T.purple },
          { l: "Groups",           v: groups.length, c: T.amber },
        ].map(k => (
          <div key={k.l} style={{
            background: T.bg2, border: `1px solid ${k.c}30`, borderRadius: 6,
            padding: "8px 16px", textAlign: "center",
          }}>
            <div style={{ fontFamily: FONTS.header, fontSize: 16, fontWeight: 700, color: k.c }}>{k.v}</div>
            <div style={{ fontSize: 7.5, color: T.text2, letterSpacing: 1, textTransform: "uppercase" }}>{k.l}</div>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div style={{
        display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap",
        padding: "12px 14px", background: T.bg2, borderRadius: 8, border: `1px solid ${T.border}`,
      }}>
        <input type="text" placeholder="🔍 Search by ID, name, or business object..."
          value={search} onChange={e => setSearch(e.target.value)}
          style={{
            background: T.bg3, border: `1px solid ${T.border}`, color: T.text,
            padding: "5px 10px", borderRadius: 4, fontSize: 10, outline: "none", minWidth: 280,
          }} />
        <select value={filterGrp} onChange={e => setFilterGrp(e.target.value)} style={selectStyle}>
          <option value="ALL">All groups</option>
          {groups.map(g => <option key={g} value={g}>{GRP_META[g]?.label || g}</option>)}
        </select>
        <div style={{ marginLeft: "auto", fontSize: 10, color: T.text2 }}>
          {interfaces.length} interfaces shown
        </div>
      </div>

      {/* Two-panel: list on left, detail on right */}
      <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 14 }}>
        {/* Left: interface list */}
        <div style={{ background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
          <div style={{
            padding: "10px 14px", borderBottom: `1px solid ${T.border}`,
            fontSize: 10, color: T.cyan, fontWeight: 700, letterSpacing: 1,
          }}>
            INTERFACES ({interfaces.length})
          </div>
          <div style={{ maxHeight: 600, overflowY: "auto" }}>
            {interfaces.map(f => {
              const grp = GRP_META[f.grp] || { color: T.steel };
              const isSel = selectedId === f.id;
              return (
                <div key={f.id} onClick={() => setSelectedId(f.id)}
                  style={{
                    padding: "8px 12px", cursor: "pointer",
                    borderBottom: `1px solid ${T.border2}`,
                    background: isSel ? `${grp.color}15` : "transparent",
                    borderLeft: `3px solid ${isSel ? grp.color : "transparent"}`,
                  }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                    <span style={{ fontFamily: FONTS.header, fontSize: 9, color: grp.color, fontWeight: 700 }}>
                      {f.id}
                    </span>
                    <span style={{
                      fontSize: 8, fontWeight: 700, color: f.contractFieldCount > 0 ? T.green : T.text3,
                    }}>
                      {f.contractFieldCount > 0 ? `${f.contractFieldCount} fields` : "no contract"}
                    </span>
                  </div>
                  <div style={{ fontSize: 10, color: isSel ? T.text : T.text2, fontWeight: isSel ? 600 : 400 }}>
                    {f.name.length > 38 ? f.name.substring(0, 38) + "…" : f.name}
                  </div>
                  <div style={{ fontSize: 8, color: T.text3, marginTop: 2 }}>
                    {f.grp} · MVP-{f.mvp}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: contract detail */}
        <div style={{ background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
          {!selected ? (
            <div style={{ padding: 40, textAlign: "center", color: T.text3, fontStyle: "italic" }}>
              ← Select an interface to view its data contract
            </div>
          ) : (
            <div>
              {/* Detail header */}
              <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.border}`, background: T.bg3 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                  <span style={{ fontFamily: FONTS.header, fontSize: 11, color: GRP_META[selected.grp]?.color, fontWeight: 700 }}>
                    {selected.id}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{selected.name}</span>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                  <span style={pillStyle(GRP_META[selected.grp]?.color || T.steel)}>{selected.grp}</span>
                  <span style={pillStyle(MVP_META[selected.mvp]?.c || T.cyan)}>MVP-{selected.mvp}</span>
                  <span style={pillStyle(T.green)}>{selectedMap.length} fields</span>
                </div>
                <div style={{ fontSize: 9.5, color: T.text2, fontFamily: "monospace" }}>
                  {selected.from} <span style={{ color: T.cyan }}>→</span> {selected.to}
                </div>
                {selected.bo && (
                  <div style={{ fontSize: 9, color: T.purple, marginTop: 4 }}>
                    📦 {selected.bo}
                  </div>
                )}
                {/* NEW: cross-tab navigation refs */}
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 10 }}>
                  <EntityRefList kind="agent"   ids={getAgentsForInterface(selected.id)}   label="🤖 Agents" />
                  <EntityRefList kind="benefit" ids={getBenefitsForInterface(selected.id)} label="🎯 Benefits" />
                  <EntityRefList kind="flow"    ids={getFlowsForInterface(selected.id)}    label="🚌 ESB flows" />
                </div>
              </div>

              {/* Category filter chips */}
              <div style={{
                padding: "10px 18px", borderBottom: `1px solid ${T.border2}`,
                display: "flex", gap: 6, flexWrap: "wrap",
              }}>
                <button onClick={() => setFilterCat("ALL")} style={catChip(filterCat === "ALL", T.cyan)}>
                  All ({selectedMap.length})
                </button>
                {allCategories.map(cat => {
                  const count = selectedMap.filter(a => (a.category || "additional") === cat).length;
                  if (count === 0) return null;
                  const color = CATEGORY_COLOR[cat];
                  return (
                    <button key={cat} onClick={() => setFilterCat(cat)}
                      style={catChip(filterCat === cat, color)}>
                      {cat} ({count})
                    </button>
                  );
                })}
              </div>

              {/* Contract table */}
              <div style={{ maxHeight: 500, overflow: "auto", padding: "10px 18px" }}>
                {selectedFiltered.length === 0 ? (
                  <div style={{ padding: 20, textAlign: "center", color: T.text3, fontStyle: "italic" }}>
                    {selectedMap.length === 0
                      ? "No data contract defined for this interface yet."
                      : "No fields match the current category filter."}
                  </div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${T.cyan}30` }}>
                        <th style={th}>Source field</th>
                        <th style={{ ...th, width: 18 }}></th>
                        <th style={th}>Destination</th>
                        <th style={th}>Transform</th>
                        <th style={th}>Category</th>
                        <th style={th}>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedFiltered.map((a, i) => {
                        const cat = a.category || "additional";
                        const catColor = CATEGORY_COLOR[cat] || T.cyan;
                        return (
                          <tr key={i} style={{ borderBottom: `1px solid ${T.border2}` }}>
                            <td style={{ ...td, color: T.cyan, fontFamily: "monospace" }}>{a.src}</td>
                            <td style={{ ...td, color: T.text3 }}>→</td>
                            <td style={{ ...td, color: T.green, fontFamily: "monospace" }}>{a.dst}</td>
                            <td style={{ ...td, color: T.amber, fontSize: 9 }}>{a.xform || "—"}</td>
                            <td style={td}>
                              <span style={pillStyle(catColor)}>{cat}</span>
                            </td>
                            <td style={{ ...td, color: T.text2, fontSize: 9, fontStyle: "italic" }}>{a.note || ""}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const selectStyle = {
  background: "#081525", border: `1px solid ${T.border}`, color: T.text,
  padding: "5px 8px", borderRadius: 4, fontSize: 9.5, outline: "none", cursor: "pointer",
};
const pillStyle = (c) => ({
  display: "inline-block", padding: "1px 7px", borderRadius: 10,
  fontSize: 8, fontFamily: FONTS.mono,
  background: `${c}15`, color: c, border: `1px solid ${c}40`,
});
const catChip = (active, color) => ({
  padding: "3px 10px", borderRadius: 10, fontSize: 8, cursor: "pointer",
  background: active ? `${color}25` : "transparent",
  border: `1px solid ${active ? color : color + "30"}`,
  color: active ? color : T.text2,
  fontFamily: FONTS.mono,
  fontWeight: active ? 700 : 400, letterSpacing: 1, textTransform: "uppercase",
});
const th = {
  padding: "6px 8px", textAlign: "left",
  fontSize: 8, color: T.text2, fontWeight: 600,
  fontFamily: FONTS.mono,
  letterSpacing: 1, textTransform: "uppercase",
};
const td = { padding: "6px 8px", fontSize: 10, color: T.text };
